import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './FieldDelegationModal.css';

const FieldDelegationModal = ({ requestId, onClose, onSuccess }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupEmails, setGroupEmails] = useState({});
  const [groupNotes, setGroupNotes] = useState({});
  const [generalNotes, setGeneralNotes] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);

  useEffect(() => {
    api.getDelegationGroups(requestId)
      .then(res => {
        const g = res.data.data.groups || [];
        setGroups(g);
        const emails = {};
        g.forEach(grp => { emails[grp.groupId] = ''; });
        setGroupEmails(emails);
      })
      .catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [requestId]);

  const toggleGroup = (groupId) => {
    setSelectedGroupIds(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedGroupIds.length === 0) {
      setError('Please select at least one group.');
      return;
    }
    for (const gid of selectedGroupIds) {
      if (!groupEmails[gid]?.trim()) {
        setError('Please provide an email address for each selected group.');
        return;
      }
    }
    setError(null);
    setSubmitting(true);
    try {
      await api.initiateDelegation(requestId, {
        selectedGroupIds,
        groupEmails,
        groupNotes,
        generalNotes: generalNotes.trim() || undefined,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fdm__overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fdm__modal">
        <div className="fdm__header">
          <h2 className="fdm__title">Request Field Input</h2>
          <button className="fdm__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {loading ? (
          <div className="fdm__loading">Loading groups…</div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="fdm__body">
              <p className="fdm__intro">
                Select the field groups you need input on, then assign a contact email for each group.
                They will receive an email with the list of fields they need to fill in.
              </p>

              <div className="fdm__general-note">
                <label className="fdm__label">General note to all delegates (optional)</label>
                <textarea
                  className="fdm__textarea"
                  rows={3}
                  value={generalNotes}
                  onChange={e => setGeneralNotes(e.target.value)}
                  placeholder="Add context or instructions for the delegates…"
                />
              </div>

              <div className="fdm__groups">
                {groups.map(group => {
                  const isSelected = selectedGroupIds.includes(group.groupId);
                  const isExpanded = expandedGroup === group.groupId;
                  return (
                    <div key={group.groupId} className={`fdm__group ${isSelected ? 'fdm__group--selected' : ''}`}>
                      <div className="fdm__group-header">
                        <label className="fdm__group-label">
                          <input
                            type="checkbox"
                            className="fdm__checkbox"
                            checked={isSelected}
                            onChange={() => toggleGroup(group.groupId)}
                          />
                          <span className="fdm__group-name">{group.label}</span>
                        </label>
                        <button
                          type="button"
                          className="fdm__expand-btn"
                          onClick={() => setExpandedGroup(isExpanded ? null : group.groupId)}
                        >
                          {isExpanded ? 'Hide fields ▲' : `${group.fields.length} fields ▼`}
                        </button>
                      </div>

                      {isExpanded && (
                        <ul className="fdm__field-list">
                          {group.fields.map(f => (
                            <li key={f.key} className="fdm__field-item">{f.label || f.key}</li>
                          ))}
                        </ul>
                      )}

                      {isSelected && (
                        <div className="fdm__group-inputs">
                          <div className="fdm__input-row">
                            <label className="fdm__label">Assign to (email) *</label>
                            <input
                              type="email"
                              className="fdm__input"
                              value={groupEmails[group.groupId] || ''}
                              onChange={e => setGroupEmails(prev => ({ ...prev, [group.groupId]: e.target.value }))}
                              placeholder="delegate@guidewire.com"
                              required
                            />
                          </div>
                          <div className="fdm__input-row">
                            <label className="fdm__label">Note for this group (optional)</label>
                            <input
                              type="text"
                              className="fdm__input"
                              value={groupNotes[group.groupId] || ''}
                              onChange={e => setGroupNotes(prev => ({ ...prev, [group.groupId]: e.target.value }))}
                              placeholder="Add a specific note for this group…"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {error && <p className="fdm__error">{error}</p>}
            </div>

            <div className="fdm__footer">
              <button type="button" className="fdm__btn fdm__btn--secondary" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="fdm__btn fdm__btn--primary" disabled={submitting || selectedGroupIds.length === 0}>
                {submitting ? 'Sending…' : 'Send Requests'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default FieldDelegationModal;
